import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ErrorData } from '@types';

type Loading =
  | 'fetch-unique-contacts-report-summary'
  | 'fetch-unique-contacts-report-price-by-range'
  | 'fetch-unique-contacts-report-contact-stats'
  | 'fetch-unique-contacts-full-report';

export type RateSummary = {
  accumulatedPrice: number;
  chargeableContacts: number;
  freeContacts: number;
};

export type PriceByRange = {
  prices: {
    date: string;
    price: number;
  }[];
};

export type ContactStats = {
  stats: {
    date: string;
    freeContacts: number;
    chargeableContacts: number;
  }[];
};

export type UniqueContactsReportState = {
  error: ErrorData | null;
  loading: Loading[];
  summary: RateSummary;
  priceByRange: PriceByRange;
  contactStats: ContactStats;
  filters: {
    startDate: string;
    endDate: string;
  };
};

export type Granularity = 'DAILY' | 'MONTHLY';

type UniqueContactsReportReducer<P = void> = (
  state: UniqueContactsReportState,
  action: PayloadAction<P>
) => void;

const initialState: UniqueContactsReportState = {
  error: null,
  loading: [],
  summary: {
    accumulatedPrice: 0,
    chargeableContacts: 0,
    freeContacts: 0,
  },
  priceByRange: {
    prices: [],
  },
  contactStats: {
    stats: [],
  },
  filters: {
    startDate: '',
    endDate: '',
  },
};

export type HandleFetchUniqueContactsSummaryReport = {
  startDate: string;
  endDate: string;
};

const handleFetchUniqueContactsSummaryReport: UniqueContactsReportReducer<
  HandleFetchUniqueContactsSummaryReport
> = state => {
  state.loading.push('fetch-unique-contacts-report-summary');
  state.error = null;
};

const handleFetchUniqueContactsReportSummarySuccess: UniqueContactsReportReducer<
  RateSummary
> = (state, action) => {
  state.summary = action.payload;
};

const handleFetchUniqueContactsReportSummaryFailure: UniqueContactsReportReducer<
  ErrorData
> = (state, action) => {
  state.error = action.payload;
};

const handleFetchUniqueContactsReportSummaryFulfill: UniqueContactsReportReducer =
  state => {
    state.loading = state.loading.filter(
      l => l !== 'fetch-unique-contacts-report-summary'
    );
  };

export type HandleFetchUniqueContactsByRangeReport =
  HandleFetchUniqueContactsSummaryReport & {
    granularity: Granularity;
  };

const handleFetchUniqueContactsByRangeReport: UniqueContactsReportReducer<
  HandleFetchUniqueContactsByRangeReport
> = state => {
  state.loading.push('fetch-unique-contacts-report-price-by-range');
  state.error = null;
};

const handleFetchUniqueContactsReportByRangeSuccess: UniqueContactsReportReducer<
  PriceByRange
> = (state, action) => {
  state.priceByRange = action.payload;
};

const handleFetchUniqueContactsReportByRangeFailure: UniqueContactsReportReducer<
  ErrorData
> = (state, action) => {
  state.error = action.payload;
};

const handleFetchUniqueContactsReportByRangeFulfill: UniqueContactsReportReducer =
  state => {
    state.loading = state.loading.filter(
      l => l !== 'fetch-unique-contacts-report-price-by-range'
    );
  };

const handleFetchUniqueContactsStatsReport: UniqueContactsReportReducer<
  HandleFetchUniqueContactsByRangeReport
> = state => {
  state.loading.push('fetch-unique-contacts-report-contact-stats');
  state.error = null;
};

const handleFetchUniqueContactsReportStatsSuccess: UniqueContactsReportReducer<
  ContactStats
> = (state, action) => {
  state.contactStats = action.payload;
};

const handleFetchUniqueContactsReportStatsFailure: UniqueContactsReportReducer<
  ErrorData
> = (state, action) => {
  state.error = action.payload;
};

const handleFetchUniqueContactsReportStatsFulfill: UniqueContactsReportReducer =
  state => {
    state.loading = state.loading.filter(
      l => l !== 'fetch-unique-contacts-report-contact-stats'
    );
  };

const handleFetchUniqueContactsFullReport: UniqueContactsReportReducer<
  HandleFetchUniqueContactsSummaryReport & {
    granularityByRange: Granularity;
    granularityByStats: Granularity;
  }
> = state => {
  state.loading.push('fetch-unique-contacts-full-report');
  state.error = null;
};

const UniqueContactsReportSlice = createSlice({
  name: 'UniqueContactsReportSlice',
  initialState,
  reducers: {
    fetchUniqueContactsSummaryReport: handleFetchUniqueContactsSummaryReport,
    fetchUniqueContactsSummaryReportSuccess:
      handleFetchUniqueContactsReportSummarySuccess,
    fetchUniqueContactsSummaryReportFailure:
      handleFetchUniqueContactsReportSummaryFailure,
    fetchUniqueContactsSummaryReportFulfill:
      handleFetchUniqueContactsReportSummaryFulfill,

    fetchUniqueContactsByRangeReport: handleFetchUniqueContactsByRangeReport,
    fetchUniqueContactsByRangeReportSuccess:
      handleFetchUniqueContactsReportByRangeSuccess,
    fetchUniqueContactsByRangeReportFailure:
      handleFetchUniqueContactsReportByRangeFailure,
    fetchUniqueContactsByRangeReportFulfill:
      handleFetchUniqueContactsReportByRangeFulfill,

    fetchUniqueContactsStatsReport: handleFetchUniqueContactsStatsReport,
    fetchUniqueContactsStatsReportSuccess:
      handleFetchUniqueContactsReportStatsSuccess,
    fetchUniqueContactsStatsReportFailure:
      handleFetchUniqueContactsReportStatsFailure,
    fetchUniqueContactsStatsReportFulfill:
      handleFetchUniqueContactsReportStatsFulfill,
    fetchUniqueContactsFullReport: handleFetchUniqueContactsFullReport,
  },
});

const { actions, reducer } = UniqueContactsReportSlice;

export const {
  fetchUniqueContactsSummaryReport,
  fetchUniqueContactsSummaryReportSuccess,
  fetchUniqueContactsSummaryReportFailure,
  fetchUniqueContactsSummaryReportFulfill,
  fetchUniqueContactsByRangeReport,
  fetchUniqueContactsByRangeReportSuccess,
  fetchUniqueContactsByRangeReportFailure,
  fetchUniqueContactsByRangeReportFulfill,
  fetchUniqueContactsStatsReport,
  fetchUniqueContactsStatsReportSuccess,
  fetchUniqueContactsStatsReportFailure,
  fetchUniqueContactsStatsReportFulfill,
  fetchUniqueContactsFullReport,
} = actions;

export default reducer;
